import React, {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import { Button, Loader, Modal } from 'ncoded-component-library';
import useCallbackRef from 'hooks/useCallbackRef';
import DownloadIcon from 'icons/Download.icon';
import { useTranslation } from 'react-i18next';
import { PdfViewer } from 'components/PdfViewer';
import shareableLinksDocuments from 'router/subrouters/ShareableLink/api/shareable-links-documents';
import {
  extractNodeWatermarkSettings,
  getUserFullName,
  isDwg,
  isEml,
  isImage,
  isOfficeFileType,
  isPdf,
  isVideo,
} from 'utils';
import useFilePreviewTrackTime from 'hooks/useFilePreviewTrackTime';
import useDisableRightClick from 'hooks/useDisableRightClick';
import useMixpanelDocumentEvents from 'hooks/useMixpanelDocumentEvents';
import OfficeFilePreview from 'components/OfficeFilePreview';
import ShareableLinkContext from 'router/subrouters/ShareableLink/providers/ShareableLink/ShareableLink.context';
import { FilePreviewWithIpAddress } from 'models/File';
import useStartDownloadingFile from 'router/subrouters/ShareableLink/hooks/useStartDownloadingFile';
import CannotPreviewInBrowser from 'components/CannotPreviewInBrowser';
import AutodeskViewer from 'components/AutodeskViewer';

import './ShareableLinkNodePreview.styles.scss';
import './ShareableLinkNodePreview.styles.responsive.scss';

type ShareableLinkNodePreviewProps = {
  className?: string;
  shareableLinkId: string;
  node: DataRoomNode;
  dataRoomName: string;
};

const ShareableLinkNodePreview: React.ForwardRefRenderFunction<
  ModalRef,
  ShareableLinkNodePreviewProps
> = (props, ref) => {
  const { className, node, shareableLinkId, dataRoomName } = props;

  const { _id: nodeId, name: documentName, canDownload, version } = node;

  const [data, setData] = useState<FilePreviewWithIpAddress>();

  const [isUrlFetching, setIsUrlFetching] = useState(false);

  const [modal, modalRef] = useCallbackRef<ModalRef>(null);

  const { mainInfo } = useContext(ShareableLinkContext);

  const isModalOpen = modal?.openState;

  const { url, type, autodeskToken, urn, isAutodeskProccessed } = data || {};

  const { firstName, lastName, email } = mainInfo || {};

  const userFullName = useMemo(
    () => getUserFullName(firstName, lastName),
    [firstName, lastName],
  );

  useDisableRightClick(isModalOpen);

  useImperativeHandle(ref, () => modal, [modal]);

  const classes = classNames('shareable-link-node-preview', className);

  const { t } = useTranslation();

  const startDownloadingFile = useStartDownloadingFile();

  const RenderTitle = useMemo(
    () =>
      canDownload && (
        <Button
          variant="outline"
          className="shareable-link-node-preview__download"
          icon={DownloadIcon}
          onClick={() => startDownloadingFile(shareableLinkId, nodeId)}
        >
          {t('download')}
        </Button>
      ),
    [canDownload, t, startDownloadingFile, shareableLinkId, nodeId],
  );

  const fetchUrl = useCallback(async () => {
    try {
      setIsUrlFetching(true);

      const { data } = await shareableLinksDocuments.getFileBufferPreview(
        shareableLinkId,
        nodeId,
      );

      if (data) {
        const { type, isAutodeskProccessed, urn } = data;

        if (isEml(type) || (isDwg(type) && (!urn || !isAutodeskProccessed)))
          startDownloadingFile(shareableLinkId, nodeId);

        setData(data);
      } else {
        modal?.close();
      }
    } catch (e) {
      modal?.close();
    } finally {
      setIsUrlFetching(false);
    }
  }, [shareableLinkId, nodeId, startDownloadingFile, modal]);

  // fetch when open, reset url on close because we want to track view count
  useEffect(() => {
    if (isModalOpen) {
      if (!data && !isUrlFetching) {
        fetchUrl();
      }

      return;
    }

    if (data) setData(null);
  }, [isUrlFetching, url, isModalOpen, fetchUrl, data]);

  const sendReport = useCallback(
    (time: number, currentPage: number) => {
      try {
        shareableLinksDocuments.trackTime(
          shareableLinkId,
          nodeId,
          currentPage,
          time,
          version || 1,
        );
      } catch (e) {
        return;
      }
    },
    [nodeId, shareableLinkId, version],
  );

  const { onDocumentViewed } = useMixpanelDocumentEvents({
    documentName,
    dataRoomName,
  });

  const { onDocumentLoad, onPageChange } = useFilePreviewTrackTime({
    isModalOpen,
    sendReport,
    onDocumentViewed,
  });

  return (
    <Modal
      className={classes}
      title={RenderTitle}
      lockScroll={false}
      ref={modalRef}
    >
      {isUrlFetching && <Loader />}

      {isEml(type) && <CannotPreviewInBrowser />}

      {isPdf(type) && (
        <PdfViewer
          url={url}
          onPageChange={onPageChange}
          onDocumentLoad={onDocumentLoad}
        />
      )}

      {isOfficeFileType(type) && (
        <OfficeFilePreview
          data={data}
          email={email}
          fullName={userFullName}
          watermarkSettings={extractNodeWatermarkSettings(node)}
          onDocumentLoad={onDocumentLoad}
        />
      )}

      {isDwg(type) &&
        (!urn || !isAutodeskProccessed ? (
          <CannotPreviewInBrowser />
        ) : (
          <AutodeskViewer
            urn={urn}
            accessToken={autodeskToken}
            email={email}
            fullName={userFullName}
            ipAddress={data?.ipAddress}
            watermarkSettings={extractNodeWatermarkSettings(node)}
            onDocumentLoad={onDocumentLoad}
          />
        ))}

      {isImage(type) && (
        <img
          alt={documentName}
          src={url}
          onLoadedData={() => onDocumentLoad()}
        />
      )}

      {isVideo(type) && (
        <video
          src={url}
          controls
          controlsList="nodownload"
          onLoadedData={() => onDocumentLoad()}
        />
      )}
    </Modal>
  );
};

export default forwardRef(ShareableLinkNodePreview);
